import { useState, useMemo, useRef, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Headphones, FileText, Users, TrendingUp, Languages, Radio, Layers, Zap, Star, Eye, Monitor, Megaphone, Share2, Plus, Check, MapPin, BookOpen, Target } from "lucide-react";

const GlobeCanvas = lazy(() => import("./GlobeCanvas"));

const LANG_COUNTRIES = {
  en: { count: 25, label: "English" }, de: { count: 6, label: "Deutsch" },
  fr: { count: 12, label: "Français" }, es: { count: 21, label: "Español" },
  it: { count: 4, label: "Italiano" }, pt: { count: 8, label: "Português" },
  ja: { count: 1, label: "日本語" }, zh: { count: 3, label: "中文" },
  ar: { count: 12, label: "العربية" }, ru: { count: 4, label: "Русский" },
};

const LOC_MAP = {
  "raa atoll": [5.6, 72.9], "baa atoll": [5.0, 72.9], "malé": [4.18, 73.51], "bali": [-8.3, 115.2],
  "phuket": [7.9, 98.3], "santorini": [36.4, 25.4], "tuscany": [43.4, 11.8], "amalfi": [40.6, 14.6],
  "como": [45.8, 9.3], "mallorca": [39.6, 2.7], "dubai": [25.2, 55.3], "seychelles": [-4.7, 55.5],
  "salzburg": [47.8, 13.0], "vienna": [48.2, 16.4], "paris": [48.9, 2.35], "london": [51.5, -0.1],
  "rome": [41.9, 12.5], "new york": [40.7, -74.0], "tokyo": [35.7, 139.7], "singapore": [1.3, 103.8],
  "maldives": [4.2, 73.5], "austria": [47.3, 13.3], "germany": [51.2, 10.4], "switzerland": [46.8, 8.2],
  "france": [46.6, 2.2], "italy": [42.5, 12.5], "spain": [40.4, -3.7], "greece": [39.1, 21.8],
  "thailand": [15.9, 100.9], "indonesia": [-2.5, 118.0], "japan": [36.2, 138.3], "uae": [23.4, 53.8],
  "india": [20.6, 78.9], "australia": [-25.3, 133.8], "united states": [39.8, -98.6], "usa": [39.8, -98.6],
  "united kingdom": [54.0, -2.0], "south africa": [-30.6, 22.9], "morocco": [31.8, -7.1],
  "portugal": [39.4, -8.2], "croatia": [45.1, 15.2], "mexico": [23.6, -102.5], "brazil": [-14.2, -51.9],
};
function getOrigin(loc) {
  if (!loc) return [47.3, 13.3];
  const l = loc.toLowerCase().trim();
  const parts = l.split(",").map(p => p.trim());
  for (const part of parts) { for (const [k, v] of Object.entries(LOC_MAP)) { if (part.includes(k)) return v; } }
  for (const [k, v] of Object.entries(LOC_MAP)) { if (l.includes(k)) return v; }
  return [47.3, 13.3];
}

/* ═══ BUSINESS-LOGIC TABS ═══ */
const MODES = [
  { key: "overview", label: "Activation Overview", labelDe: "Aktivierungs-Übersicht", icon: Zap, color: "#2BFECD" },
  { key: "assets", label: "Story Infrastructure", labelDe: "Story-Infrastruktur", icon: Layers, color: "#A855F7" },
  { key: "distribution", label: "Global Distribution", labelDe: "Globale Distribution", icon: Globe, color: "#3B82F6" },
  { key: "sales", label: "Sales Enablement", labelDe: "Sales Enablement", icon: TrendingUp, color: "#C9A96E" },
  { key: "markets", label: "Markets & Expansion", labelDe: "Märkte & Expansion", icon: Languages, color: "#3B82F6" },
];

/* ═══ TAB-SPECIFIC ACTIVATION PRODUCTS (right side) ═══ */
const TAB_PRODUCTS = {
  overview: [
    { id: "dedicated_page", en: "Dedicated Property Page", de: "Dedizierte Property-Seite", price: 2500, icon: FileText, color: "#2BFECD", desc_en: "Your own story hub. Sales-ready, direct traffic.", desc_de: "Ihr eigener Story-Hub. Vertriebsfähig, Direct-Traffic." },
    { id: "b2b_onepager", en: "B2B Presentation Layer", de: "B2B Präsentations-Ebene", price: 1500, icon: Monitor, color: "#3B82F6", desc_en: "Roadshow-ready. Advisor-forwardable.", desc_de: "Roadshow-fertig. Berater-weiterleitbar." },
    { id: "paid_activation", en: "Paid Activation", de: "Bezahlte Aktivierung", price: 5000, icon: Megaphone, color: "#A855F7", desc_en: "Story-to-ad pipeline. Narrative campaigns.", desc_de: "Story-to-Ad Pipeline. Narrative Kampagnen." },
  ],
  assets: [
    { id: "dedicated_page", en: "Dedicated Property Page", de: "Dedizierte Property-Seite", price: 2500, icon: FileText, color: "#2BFECD", desc_en: "Controlled story surface. No competitor noise.", desc_de: "Kontrollierte Story-Oberfläche. Keine Konkurrenz." },
    { id: "iframe_embed", en: "Embeddable Story Layer", de: "Einbettbare Story-Ebene", price: 3500, icon: Layers, color: "#A855F7", desc_en: "iFrame integration into your hotel website.", desc_de: "iFrame-Integration in Ihre Hotel-Website." },
    { id: "buyout_rights", en: "Own-Channel Usage Rights", de: "Eigenkanal-Nutzungsrechte", price: 8000, icon: Star, color: "#C9A96E", desc_en: "Full own-channel placement. Annual license.", desc_de: "Volle Eigenkanal-Platzierung. Jahreslizenz." },
  ],
  distribution: [
    { id: "partner_distribution", en: "Partner Distribution", de: "Partner-Distribution", price: 2000, icon: Share2, color: "#C9A96E", desc_en: "Newsletter, trade, roadshow ecosystem.", desc_de: "Newsletter, Handel, Roadshow-Ökosystem." },
    { id: "paid_activation", en: "Paid Activation", de: "Bezahlte Aktivierung", price: 5000, icon: Megaphone, color: "#A855F7", desc_en: "Narrative-led digital campaigns.", desc_de: "Narrative digitale Kampagnen." },
    { id: "advisor_network", en: "Travel Advisor Network", de: "Reiseberater-Netzwerk", price: 2000, icon: Users, color: "#2BFECD", desc_en: "B2B advisor & concierge ecosystem.", desc_de: "B2B Berater- & Concierge-Ökosystem." },
  ],
  sales: [
    { id: "b2b_onepager", en: "B2B Presentation Layer", de: "B2B Präsentations-Ebene", price: 1500, icon: Monitor, color: "#3B82F6", desc_en: "Trade show, roadshow, advisor briefing.", desc_de: "Messe, Roadshow, Berater-Briefing." },
    { id: "advisor_network", en: "Travel Advisor Network", de: "Reiseberater-Netzwerk", price: 2000, icon: Users, color: "#2BFECD", desc_en: "Agent-facing sales utility.", desc_de: "Berater-orientierter Vertriebsnutzen." },
    { id: "dedicated_page", en: "Dedicated Sales Surface", de: "Dedizierte Vertriebsoberfläche", price: 2500, icon: FileText, color: "#C9A96E", desc_en: "Pitch-ready property story destination.", desc_de: "Pitch-bereite Property Story Destination." },
  ],
  markets: [
    { id: "dedicated_page", en: "Multi-Language Story Page", de: "Mehrsprachige Story-Seite", price: 2500, icon: FileText, color: "#2BFECD", desc_en: "One page, multiple language versions.", desc_de: "Eine Seite, mehrere Sprachversionen." },
    { id: "partner_distribution", en: "Cross-Market Distribution", de: "Cross-Market-Distribution", price: 2000, icon: Share2, color: "#C9A96E", desc_en: "International partner ecosystem reach.", desc_de: "Internationales Partner-Ökosystem." },
  ],
};

/* ═══ CART-DRIVEN STATE ═══ */
function useActivationState(props) {
  return useMemo(() => {
    const { selectedTier, selectedPodcasts, podcastLangs, selectedEpisodes, videoLangs, hostedStay, onsiteLangs, gmStory, chefStory, selectedPremiumModules, selectedOnsiteHybridEps, viralProducts = [] } = props;
    const langSet = new Set(["en"]);
    selectedPodcasts.forEach(id => (podcastLangs[id] || ["en"]).forEach(l => langSet.add(l)));
    selectedEpisodes.forEach(id => (videoLangs[id] || ["en"]).forEach(l => langSet.add(l)));
    Object.keys(onsiteLangs).forEach(k => (onsiteLangs[k] || []).forEach(l => langSet.add(l)));
    const activeLangs = [...langSet];
    let countries = 0;
    activeLangs.forEach(l => { countries += (LANG_COUNTRIES[l]?.count || 0); });

    let assetCount = 0;
    if (selectedPodcasts.length > 0) assetCount++;
    if (selectedEpisodes.length > 0 || selectedOnsiteHybridEps.length > 0) assetCount++;
    if (hostedStay) assetCount++;
    if (gmStory || chefStory) assetCount++;
    viralProducts.forEach(id => { if (["dedicated_page", "iframe_embed", "buyout_rights"].includes(id)) assetCount++; });

    const hasPodcast = selectedPodcasts.length > 0;
    const hasVideo = selectedEpisodes.length > 0 || selectedOnsiteHybridEps.length > 0;
    const hasEditorial = hostedStay;
    const hasAny = hasPodcast || hasVideo || hasEditorial || gmStory || chefStory;

    const depth = assetCount >= 5 ? "Full Ecosystem" : assetCount >= 3 ? "Extended" : assetCount >= 1 ? "Base" : "—";

    let score = 10;
    if (hasPodcast) score += 10; if (hasVideo) score += 10; if (hasEditorial) score += 10;
    if (gmStory || chefStory) score += 6;
    if (activeLangs.length > 1) score += Math.min(activeLangs.length * 3, 12);
    if (selectedTier === "signature") score += 4; if (selectedTier === "icon") score += 8;
    viralProducts.forEach(() => { score += 5; });
    score = Math.min(score, 100);

    return { activeLangs, countries, assetCount, hasPodcast, hasVideo, hasEditorial, hasAny, score, viralProducts, selectedTier, depth };
  }, [props]);
}

/* ═══ STATUS KPI CARD (left side) — large, readable ═══ */
function StatusCard({ label, value, sub, icon: Icon, color, active }) {
  return (
    <div className="flex items-center gap-4 py-3">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: active ? `${color}14` : 'rgba(255,255,255,0.02)', border: `1px solid ${active ? color + '22' : 'rgba(255,255,255,0.04)'}` }}>
        <Icon className="w-5 h-5" style={{ color: active ? color : 'rgba(255,255,255,0.12)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white/40 text-xs font-semibold tracking-wider uppercase mb-0.5">{label}</p>
        <div className="flex items-baseline gap-2">
          <p className="font-bold text-2xl leading-none" style={{ color: active ? color : 'rgba(255,255,255,0.15)' }}>{value}</p>
          {sub && <p className="text-white/25 text-xs">{sub}</p>}
        </div>
      </div>
      {active && <div className="w-2 h-2 rounded-full animate-pulse shrink-0" style={{ background: color }} />}
    </div>
  );
}

/* ═══ PRODUCT ACTION CARD (right side) — readable ═══ */
function ProductCard({ prod, isActive, lang, onToggle, pricingVisible }) {
  const Icon = prod.icon;
  return (
    <button onClick={() => onToggle(prod.id)}
      className="w-full flex items-start gap-4 p-4 rounded-xl border text-left transition-all duration-200 hover:bg-white/[0.02]"
      style={{ borderColor: isActive ? `${prod.color}28` : 'rgba(255,255,255,0.04)', background: isActive ? `${prod.color}08` : 'transparent' }}
      data-testid={`viral-product-${prod.id}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: isActive ? `${prod.color}14` : 'rgba(255,255,255,0.02)', border: `1px solid ${isActive ? prod.color + '28' : 'rgba(255,255,255,0.04)'}` }}>
        {isActive ? <Check className="w-4 h-4" style={{ color: prod.color }} /> : <Plus className="w-4 h-4 text-white/15" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className={`text-sm font-semibold leading-tight ${isActive ? 'text-white/80' : 'text-white/40'}`}>
            {lang === "de" ? prod.de : prod.en}
          </p>
          <span className="text-sm font-bold ml-3 shrink-0" style={{ color: isActive ? prod.color : 'rgba(255,255,255,0.18)' }}>
            {pricingVisible ? `€${prod.price.toLocaleString()}` : null}
          </span>
        </div>
        <p className="text-white/25 text-xs leading-relaxed">{lang === "de" ? prod.desc_de : prod.desc_en}</p>
      </div>
    </button>
  );
}

/* ═══ EXACTLY 12 KPIs — 2–3 per Business-Logic Tab ═══ */
function getKpis(mode, s) {
  const sl = s.activeLangs.length > 1 ? s.activeLangs.map(l => l.toUpperCase()).join(" · ") : "";
  const kpis = {
    // Tab 1 — Activation Overview: 3 KPIs
    overview: [
      { label: "Activation Level", value: `${s.score}%`, icon: Zap, color: "#2BFECD", active: s.score > 15 },
      { label: "Story Assets Live", value: `${s.assetCount}`, sub: s.depth, icon: Layers, color: "#A855F7", active: s.assetCount > 0 },
      { label: "Active Languages", value: `${s.activeLangs.length}`, sub: sl, icon: Languages, color: "#3B82F6", active: true },
    ],
    // Tab 2 — Story Infrastructure: 2 KPIs
    assets: [
      { label: "Content Depth", value: s.depth, icon: Layers, color: "#A855F7", active: s.assetCount > 0 },
      { label: "Active Platforms", value: s.hasPodcast ? "12+" : s.hasVideo ? "6+" : "—", sub: s.hasPodcast ? "Spotify · Apple · Amazon" : "", icon: Radio, color: "#C9A96E", active: s.hasPodcast || s.hasVideo },
    ],
    // Tab 3 — Global Distribution: 3 KPIs
    distribution: [
      { label: "Podcast Reach", value: s.hasPodcast ? "12+ Platforms" : "—", icon: Radio, color: "#A855F7", active: s.hasPodcast },
      { label: "Partner Channels", value: s.viralProducts.includes("partner_distribution") ? "Active" : "—", icon: Share2, color: "#C9A96E", active: s.viralProducts.includes("partner_distribution") },
      { label: "Paid Activation", value: s.viralProducts.includes("paid_activation") ? "Active" : "—", icon: Megaphone, color: "#2BFECD", active: s.viralProducts.includes("paid_activation") },
    ],
    // Tab 4 — Sales Enablement: 2 KPIs
    sales: [
      { label: "B2B Readiness", value: s.viralProducts.includes("b2b_onepager") ? "Ready" : "—", icon: TrendingUp, color: "#C9A96E", active: s.viralProducts.includes("b2b_onepager") },
      { label: "Advisor Network", value: s.viralProducts.includes("advisor_network") ? "Active" : "—", icon: Users, color: "#2BFECD", active: s.viralProducts.includes("advisor_network") },
    ],
    // Tab 5 — Markets & Expansion: 2 KPIs
    markets: [
      { label: "Reachable Countries", value: `${s.countries}`, icon: Globe, color: "#2BFECD", active: s.countries > 0 },
      { label: "International Readiness", value: s.activeLangs.length > 2 ? "High" : s.activeLangs.length > 1 ? "Medium" : "Basic", icon: Star, color: "#3B82F6", active: s.activeLangs.length > 1 },
    ],
  };
  return kpis[mode] || kpis.overview;
}

/* ═══ MAIN COMPONENT ═══ */
export default function ViralActivation(props) {
  const { t, lang, Fade, hotelName, hotelLocation, pricingVisible = true } = props;
  const [activeMode, setActiveMode] = useState("overview");
  const [viralProducts, setViralProducts] = useState([]);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const allProps = { ...props, viralProducts };
  const state = useActivationState(allProps);
  const kpis = getKpis(activeMode, state);
  const tabProducts = TAB_PRODUCTS[activeMode] || TAB_PRODUCTS.overview;
  const activeConfig = MODES.find(m => m.key === activeMode);
  const originCoords = useMemo(() => getOrigin(hotelLocation), [hotelLocation]);

  const showToast = (msg, color) => {
    setToast({ message: msg, color });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2200);
  };
  const toggleProduct = (id) => {
    const all = Object.values(TAB_PRODUCTS).flat();
    const prod = all.find(p => p.id === id);
    if (viralProducts.includes(id)) {
      setViralProducts(v => v.filter(x => x !== id));
      showToast(lang === "de" ? "Entfernt" : "Removed", "#ff6b6b");
    } else {
      setViralProducts(v => [...v, id]);
      showToast(`${lang === "de" ? prod?.de : prod?.en} activated`, "#2BFECD");
    }
  };
  const viralTotal = viralProducts.reduce((s, id) => {
    const p = Object.values(TAB_PRODUCTS).flat().find(x => x.id === id);
    return s + (p?.price || 0);
  }, 0);

  return (
    <div className="relative w-full" data-testid="viral-activation-dashboard">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.3) 100%)' }} />
      </div>

      {/* ═══ HEADER ═══ */}
      <Fade>
        <div className="text-center mb-6 relative z-10">
          <p className="text-[#2BFECD]/40 text-xs font-bold tracking-[0.4em] uppercase mb-3">BEYOND THE PLATFORM</p>
          <h2 className="text-4xl lg:text-5xl font-light leading-none mb-3 text-white/90">Global Story Activation</h2>
          <p className="text-white/30 text-base max-w-2xl mx-auto">
            {t(
              "Your story enters the world's listening environments — across languages, markets and cultural touchpoints.",
              "Ihre Story betritt die Hörumgebungen der Welt — über Sprachen, Märkte und kulturelle Berührungspunkte."
            )}
          </p>
        </div>
      </Fade>

      {/* ═══ TABS — business logic, large readable labels ═══ */}
      <div className="relative z-10 flex justify-center mb-8">
        <div className="inline-flex gap-1.5 p-2 rounded-2xl border border-white/[0.04] bg-white/[0.015]" data-testid="mode-tabs">
          {MODES.map(mode => {
            const Icon = mode.icon;
            const a = activeMode === mode.key;
            return (
              <button key={mode.key} onClick={() => setActiveMode(mode.key)} data-testid={`mode-tab-${mode.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold tracking-wide uppercase transition-all ${a ? 'text-white' : 'text-white/25 hover:text-white/40'}`}
                style={a ? { background: `${mode.color}14`, border: `1px solid ${mode.color}20` } : { border: '1px solid transparent' }}>
                <Icon className="w-4 h-4" style={a ? { color: mode.color } : {}} />
                <span className="hidden lg:inline">{lang === "de" ? mode.labelDe : mode.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ MAIN STAGE: Left Status | Center Globe | Right Actions ═══ */}
      <div className="relative z-10 grid grid-cols-12 gap-6 items-start" style={{ minHeight: 440 }}>

        {/* LEFT = Status / KPIs */}
        <div className="col-span-3 pt-2">
          <AnimatePresence mode="wait">
            <motion.div key={activeMode} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <p className="text-white/20 text-xs font-bold tracking-[0.2em] uppercase mb-4">
                {lang === "de" ? "STATUS" : "STATUS"}
              </p>
              {kpis.map((k, i) => <StatusCard key={i} {...k} />)}
              <div className="mt-5 pt-4 border-t border-white/[0.05]">
                <p className="text-[#2BFECD]/30 text-xs font-bold tracking-[0.15em] uppercase mb-1.5">PROPERTY ORIGIN</p>
                <p className="text-white/50 text-sm font-semibold truncate">{hotelName}</p>
                <p className="text-white/22 text-xs">{hotelLocation}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* CENTER = Emotion / Globe */}
        <div className="col-span-6 relative" data-testid="commercial-globe-container">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.div animate={{ scale: [1, 1.03, 1], opacity: [0.08, 0.14, 0.08] }} transition={{ duration: 6, repeat: Infinity }}
              className="w-[380px] h-[380px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(43,254,205,0.12), transparent 55%)' }} />
          </div>
          <div className="h-[400px] lg:h-[440px]">
            <Suspense fallback={<div className="w-full h-full flex items-center justify-center"><div className="w-28 h-28 rounded-full border border-[#2BFECD]/10 animate-pulse" /></div>}>
              <GlobeCanvas mode={activeMode} originCoords={originCoords} />
            </Suspense>
          </div>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 pointer-events-none">
            <AnimatePresence mode="wait">
              <motion.p key={activeMode} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: `${activeConfig?.color || '#2BFECD'}35` }}>
                {lang === "de" ? activeConfig?.labelDe : activeConfig?.label}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>

        {/* RIGHT = Actionable Upsells / Cart Products */}
        <div className="col-span-3 pt-2">
          <AnimatePresence mode="wait">
            <motion.div key={activeMode} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <p className="text-white/20 text-xs font-bold tracking-[0.2em] uppercase mb-4">
                {lang === "de" ? "AKTIVIERUNGS-PRODUKTE" : "ACTIVATION PRODUCTS"}
              </p>
              <div className="space-y-2.5">
                {tabProducts.map(prod => (
                  <ProductCard key={prod.id} prod={prod} isActive={viralProducts.includes(prod.id)} lang={lang} onToggle={toggleProduct} pricingVisible={pricingVisible} />
                ))}
              </div>
              {viralTotal > 0 && (
                <div className="mt-4 pt-4 border-t border-white/[0.05] flex items-center justify-between">
                  <span className="text-white/30 text-xs font-bold tracking-wider uppercase">{viralProducts.length} {lang === "de" ? "aktiv" : "active"}</span>
                  {pricingVisible && <span className="text-[#2BFECD]/70 text-lg font-bold">€{viralTotal.toLocaleString()}</span>}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* ═══ BOTTOM — Executive Summary Dashboard ═══ */}
      <div className="relative z-10 mt-8 mx-auto max-w-4xl">
        <div className="rounded-2xl border border-white/[0.04] overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(8,14,24,0.85), rgba(4,8,14,0.92))' }} data-testid="bottom-dashboard-strip">
          <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, #2BFECD12, #3B82F612, transparent)' }} />
          <div className="px-8 py-5 flex items-center gap-8">
            {[
              { l: lang === "de" ? "Sprachen" : "Languages", v: `${state.activeLangs.length}`, c: "#3B82F6" },
              { l: lang === "de" ? "Länder" : "Countries", v: `${state.countries}`, c: "#2BFECD" },
              { l: lang === "de" ? "Assets" : "Assets", v: `${state.assetCount}`, c: "#A855F7" },
              { l: "B2B", v: state.viralProducts.includes("b2b_onepager") ? "Ready" : "—", c: "#C9A96E" },
              { l: "Roadshow", v: (state.viralProducts.includes("b2b_onepager") && state.hasAny) ? "Ready" : "—", c: "#3B82F6" },
            ].map((m, i) => (
              <div key={i} className="text-center flex-1" data-testid={`bottom-metric-${i}`}>
                <p className="text-white/22 text-xs font-medium tracking-wider uppercase mb-1">{m.l}</p>
                <p className="font-bold text-2xl" style={{ color: m.c }}>{m.v}</p>
              </div>
            ))}
            <div className="flex-[2] flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full bg-white/[0.04] overflow-hidden">
                <motion.div className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #2BFECD, #3B82F6, #A855F7)' }}
                  key={state.score} initial={{ width: "0%" }} animate={{ width: `${state.score}%` }} transition={{ duration: 1.2, ease: "easeOut" }} />
              </div>
              <p className="text-white/30 text-lg font-bold shrink-0">{state.score}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 rounded-xl border backdrop-blur-xl"
            style={{ background: 'rgba(6,12,20,0.92)', borderColor: `${toast.color}30` }}>
            <p className="text-sm font-semibold" style={{ color: toast.color }}>{toast.message}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
